/**
 * src/composables/useUtils.js
 * * Este composable centraliza funciones de utilidad global, como formato de moneda y fechas.
 */
// Nota: No necesitamos 'ref' ni 'reactive' si solo exportamos funciones puras.

/**
 * Formatea un valor numérico como cadena de moneda, usando un símbolo configurable.
 * @param {number} value - El valor a formatear.
 * @param {string} [currencyCode='USD'] - Código ISO de la moneda (usado para Intl, aunque el símbolo es custom).
 * @param {string} [currencySymbol='$'] - Símbolo de moneda a prependizar.
 * @returns {string} El valor formateado con el símbolo.
 */
export function formatCurrency(value, currencyCode = 'USD', currencySymbol = '$') {
    if (value === null || isNaN(value)) {
        value = 0;
    }

    if (!currencyCode){
        currencyCode='UDS'
    }
    //console.log(currencyCode); // Comentamos el console.log para mantener limpio el entorno de producción

    // Usamos Intl.NumberFormat para el formato de miles/decimales
    const formattedValue = new Intl.NumberFormat('en-US', {
        // La configuración 'en-US' generalmente usa coma para miles y punto para decimales,
        // lo cual es común en la mayoría de los sistemas POS.
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);

    return `${currencySymbol} ${formattedValue}`;
}

/**
 * Formatea una cadena de fecha a un formato local legible (dd/mm/yyyy hh:mm).
 * @param {string} dateString - La cadena de fecha (ej: ISO string).
 * @returns {string} La fecha y hora formateadas.
 */
export function formatDate(dateString) {
    if (!dateString) {
        return 'Fecha no disponible';
    }
    const date = new Date(dateString);
    if (isNaN(date)) {
        return 'Fecha inválida';
    }
    return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

export function currentLocalDate(date = new Date()){
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Mesi (0-11) + 1
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * Carga una imagen de una URL y la convierte a una cadena Base64.
 * Esto es necesario para añadir imágenes externas a jsPDF.
 * * @param {string} url - La URL de la imagen a cargar (ej: logoUrl).
 * @param {string} [mimeType='image/png'] - El tipo MIME de la imagen (ej: 'image/jpeg', 'image/png').
 * @returns {Promise<string|null>} La cadena Base64 o null si falla.
 */
export const getImageBase64 = (url, mimeType = 'image/png') => {
    if (!url) return Promise.resolve(null);

    return new Promise((resolve) => {
        const img = new Image();

        // Permite cargar imágenes de dominios diferentes (CORS)
        img.crossOrigin = 'Anonymous';

        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);

                // Devuelve la Data URL (Base64) con el tipo MIME especificado
                resolve(canvas.toDataURL(mimeType));
            } catch (error) {
                console.error("Error processing image canvas:", error);
                resolve(null);
            }
        };

        img.onerror = () => {
            console.warn(`Failed to load image from URL: ${url}`);
            resolve(null); // Resuelve a null si la carga falla
        };

        img.src = url;
    });
};

export function calcAge(data){
    let d = new Date(),
    month = '' + (d.getMonth() + 1),
    day = '' + d.getDate(),
    year = d.getFullYear();

    // Formatear fecha actual (AAAA-MM-DD)
    if (month.length < 2)
        month = '0' + month;
    if (day.length < 2)
        day = '0' + day;

    d = [year, month, day].join('-');

    /*------------*/
    let nowDate = new Date(d);
    let birthDate = new Date(data);

    // --- Cálculo Inicial de Años, Meses y Días ---
    let age = nowDate.getFullYear() - birthDate.getFullYear();
    let months = nowDate.getMonth() - birthDate.getMonth();
    let days = nowDate.getDate() - birthDate.getDate();

    // Ajuste de Meses y Años
    if (months < 0) {
        age--;
        months = 12 + months;
    }

    // Ajuste de Días y Meses
    if (days < 0) {
        // Días en el mes anterior
        let daysInLastMonth = new Date(nowDate.getFullYear(), nowDate.getMonth(), 0).getDate();

        months--;
        days = daysInLastMonth + days;
    }

    // Ajuste final si la resta de días forzó a meses a ser negativo (raro, pero seguro)
    if (months < 0) {
        months = 11;
        age--;
    }

    // --- LÓGICA DE SINGULAR/PLURAL ---
    const yearLabel = age === 1 ? 'año' : 'años';
    const monthLabel = months === 1 ? 'mes' : 'meses';
    const dayLabel = days === 1 ? 'día' : 'días';

    // --- RETORNO INTEGRADO Y CORREGIDO ---
    return `${age} ${yearLabel}, ${months} ${monthLabel}, ${days} ${dayLabel}`;
};

export default {
    install(app) {
        // Exponerlas en las propiedades globales
        app.config.globalProperties.$formatCurrency = formatCurrency;
        app.config.globalProperties.$formatDate = formatDate;
        app.config.globalProperties.$getImageBase64 = getImageBase64;
        app.config.globalProperties.$currentLocalDate = currentLocalDate;
        app.config.globalProperties.$calcAge = calcAge;
    }
};